import React from 'react';
import {Button, CircularProgress, Tooltip} from "@mui/material";
import {useTranslation} from "react-i18next";
import {PrintRounded} from "@mui-symbols-material/w300";
import {useContaminationDeclarationPrint} from "../../hooks/useContaminationDeclarationPrint";

interface Props {
    declarationId: string;
    /** Blocked for a reason the caller explains in `tooltip` (e.g. unsaved changes). */
    disabled?: boolean;
    /** Replaces the default hint; say *why* when the button is disabled. */
    tooltip?: string;
    variant?: 'text' | 'outlined' | 'contained';
    size?: 'small' | 'medium';
    /** Stretches button *and* tooltip wrapper, e.g. in the sidebar column. */
    fullWidth?: boolean;
    'data-testid'?: string;
}

/**
 * "Print" for a declaration of contamination: opens the PDF the backend renders
 * from the *saved* answers in a new tab, where the user prints it and signs it
 * by hand. Used by the editor footer, the event drawer and the asset section —
 * the event row menu drives the same hook from a menu item instead.
 */
const PrintDeclarationButton = ({
    declarationId,
    disabled = false,
    tooltip,
    variant = 'outlined',
    size = 'small',
    fullWidth = false,
    'data-testid': testId = 'doc-print',
}: Props) => {
    const {t} = useTranslation();
    const {print, busy} = useContaminationDeclarationPrint();
    const isDisabled = disabled || busy;

    const button = (
        <Button
            variant={variant}
            size={size}
            fullWidth={fullWidth}
            onClick={() => print(declarationId)}
            disabled={isDisabled}
            startIcon={busy ? <CircularProgress size={16} color="inherit"/> : <PrintRounded fontSize="small"/>}
            data-testid={testId}
        >
            {t('Print')}
        </Button>
    );

    // A disabled MUI button fires no mouse events, so the tooltip needs a live
    // element around it — otherwise the explanation never shows when it matters.
    // The wrapper has to stretch too, or `fullWidth` has nothing to fill.
    return (
        <Tooltip title={tooltip ?? t('Opens the PDF in a new tab.')}>
            <span style={fullWidth ? {display: 'block', width: '100%'} : undefined}>{button}</span>
        </Tooltip>
    );
};

export default PrintDeclarationButton;
